/**
 * Portal API helpers
 * - expose a shared ThreadsClient shim that normalizes API bases per module
 * - expose utilities for determining the portal API base path
 */
// Note: For derivative apps, these should be imported from @korvo/portal-sdk
// In the main repo, @korvo/portal-sdk is available via workspace:* dependency
// Use local alias to avoid resolver issues in tests/build

import PortalThreadsClient, {
  getPortalApiBase as sdkGetPortalApiBase,
  ThreadsClient,
  useThread as portalUseThread,
} from '@korvo/portal-sdk/threads-client'

import { getAvailableModulesFromProvider } from '../../config/module-registry-provider'

function getAvailableModules(): string[] {
  try {
    return getAvailableModulesFromProvider()
  } catch {
    return []
  }
}

function ensureAvailableModules(): string[] {
  const modules = getAvailableModules()
  if (modules.length === 0) {
    // Client may load before instrumentation; use chat as fallback (portal settings live there)
    return ['chat']
  }
  return modules
}

let cachedDefaultPortalApiBase: string | null = null

// Dynamic default: use the first available module from the generated registry
function resolveDefaultPortalApiBase(): string {
  if (cachedDefaultPortalApiBase) {
    return cachedDefaultPortalApiBase
  }

  const availableModules = ensureAvailableModules()
  cachedDefaultPortalApiBase = `/api/${availableModules[0]}`
  return cachedDefaultPortalApiBase
}

export function getPortalApiBase(apiBase?: string, pathname?: string): string {
  const hasCustomBase = apiBase !== undefined && apiBase !== null && `${apiBase}`.length > 0

  // Delegate to SDK when explicit apiBase (client-safe; no registry required).
  if (hasCustomBase) {
    return sdkGetPortalApiBase(apiBase!)
  }

  const availableModules = ensureAvailableModules()
  const defaultBase = resolveDefaultPortalApiBase()

  // When using the default base (no explicit apiBase), allow pathname to drive module selection
  if (pathname) {
    const [, firstSegment] = pathname.split('/')
    if (firstSegment) {
      if (availableModules.includes(firstSegment)) {
        return sdkGetPortalApiBase(`/api/${firstSegment}`)
      }
      throw new Error(
        `Unknown portal module "${firstSegment}". Allowed modules: ${availableModules.join(', ') ||
          'none'}`,
      )
    }
  }

  return sdkGetPortalApiBase(defaultBase)
}

/** Module-aware threads client; use this when you need default API base from registry. For plain SDK behavior, use ThreadsClient from @korvo/portal-sdk/threads-client. */
export class KorvoThreadsClient extends PortalThreadsClient {
  constructor(baseUrl = '', apiBase?: string) {
    // If no apiBase provided, use dynamic default based on available modules
    const resolvedApiBase = getPortalApiBase(apiBase)
    super(baseUrl, resolvedApiBase)
  }
}

export const useThread = portalUseThread

export { ThreadsClient }

